import React, { useState, useCallback, useEffect, useMemo } from 'react'
import { ChevronDown, Search } from 'lucide-react'
import { designTokens } from '@/utils/design-tokens'
import { HistoryActionService } from '@/api/generated/services/HistoryActionService'

interface ActionModelSelectProps {
  selectedActionModel: string
  selectedActionModelName: string
  onSelect: (actionModel: string, actionModelName: string) => void
  disabled?: boolean
  error?: string | null
  placeholder?: string
  showAllOption?: boolean
}

interface ActionModelOption {
  key: string
  name: string
}

const ActionModelSelect: React.FC<ActionModelSelectProps> = ({
  selectedActionModel,
  selectedActionModelName,
  onSelect,
  disabled = false,
  error = null,
  placeholder = '변경 대상을 선택하세요',
  showAllOption = false
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [actionModels, setActionModels] = useState<ActionModelOption[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [fetchError, setFetchError] = useState<string | null>(null)

  // Fetch action models
  const fetchActionModels = useCallback(async () => {
    setIsLoading(true)
    setFetchError(null)

    try {
      const response = await HistoryActionService.appsHistoryViewsListActionModels()

      if (response) {
        // Convert Record<string, string> to ActionModelOption[]
        const options: ActionModelOption[] = Object.entries(response).map(([key, name]) => ({
          key,
          name: String(name)
        }))
        setActionModels(options)
      }
    } catch (err) {
      console.error('Failed to fetch action models:', err)
      setFetchError('변경 대상 목록을 불러오는데 실패했습니다.')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchActionModels()
  }, [fetchActionModels])

  // Filter action models based on search term
  const filteredActionModels = useMemo(() => {
    if (!searchTerm.trim()) return actionModels
    const searchLower = searchTerm.toLowerCase().trim()
    return actionModels.filter(am =>
      am.name.toLowerCase().includes(searchLower) ||
      am.key.toLowerCase().includes(searchLower)
    )
  }, [actionModels, searchTerm])

  const handleSelect = (actionModel: ActionModelOption) => {
    onSelect(actionModel.key, actionModel.name)
    setIsOpen(false)
    setSearchTerm('')
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onSelect('', '')
    setSearchTerm('')
  }

  const handleOpen = () => {
    if (!disabled) {
      setIsOpen(!isOpen)
      if (!isOpen) {
        setSearchTerm('')
      }
    }
  }

  return (
    <div className="relative">
      {/* Selection Button */}
      <button
        type="button"
        onClick={handleOpen}
        disabled={disabled}
        className="w-full flex items-center gap-1.5 px-3 py-0 h-10 border-2 rounded cursor-pointer transition-all duration-150 text-left justify-between"
        style={{
          backgroundColor: disabled ? designTokens.colors.gray[0] : designTokens.colors.white,
          borderColor: error ? designTokens.colors.error.DEFAULT : designTokens.colors.gray[1],
          fontFamily: designTokens.typography.fontFamily.sans[0],
          fontSize: designTokens.typography.fontSize['body-medium'][0],
          lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
          color: selectedActionModelName ? designTokens.colors.gray[6] : designTokens.colors.gray[3]
        }}
      >
        <div className="flex-1 flex justify-between items-center">
          <span className="truncate">{selectedActionModelName || placeholder}</span>
          <div className="flex items-center gap-2">
            {selectedActionModel && selectedActionModel.trim() !== '' && (
              <span
                onClick={handleClear}
                className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                style={{ fontSize: '14px' }}
              >
                ✕
              </span>
            )}
            <ChevronDown
              className="w-5 h-5"
              style={{ color: designTokens.colors.gray[3] }}
            />
          </div>
        </div>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className="absolute z-50 w-full mt-1 bg-white border-2 rounded shadow-lg"
          style={{
            borderColor: designTokens.colors.gray[1]
          }}
        >
          {/* Search Input */}
          <div className="p-3 border-b" style={{ borderBottomColor: designTokens.colors.gray[1] }}>
            <div className="relative">
              <Search
                className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4"
                style={{ color: designTokens.colors.gray[3] }}
              />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="변경 대상 검색..."
                className="w-full pl-2 pr-3 py-2 border rounded"
                style={{
                  borderColor: designTokens.colors.gray[1],
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: '14px',
                  color: designTokens.colors.gray[6]
                }}
                onClick={(e) => e.stopPropagation()}
                autoFocus
              />
            </div>
          </div>

          {/* Dropdown Content */}
          <div className="max-h-48 overflow-y-auto">
            {isLoading ? (
              <div
                className="p-3 text-center"
                style={{
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: '16px',
                  color: designTokens.colors.gray[4]
                }}
              >
                변경 대상 목록을 불러오는 중...
              </div>
            ) : fetchError ? (
              <div
                className="p-3 text-center"
                style={{
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: '16px',
                  color: designTokens.colors.error.DEFAULT
                }}
              >
                {fetchError}
              </div>
            ) : actionModels.length === 0 ? (
              <div
                className="p-3 text-center"
                style={{
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: '16px',
                  color: designTokens.colors.gray[4]
                }}
              >
                변경 대상이 없습니다.
              </div>
            ) : filteredActionModels.length === 0 ? (
              <div
                className="p-3 text-center"
                style={{
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: '16px',
                  color: designTokens.colors.gray[4]
                }}
              >
                검색 결과가 없습니다.
              </div>
            ) : (
              <>
                {/* All Option */}
                {showAllOption && !searchTerm.trim() && (
                  <button
                    type="button"
                    onClick={() => {
                      onSelect('', '')
                      setIsOpen(false)
                      setSearchTerm('')
                    }}
                    className="w-full px-3 py-2 text-left hover:bg-gray-50 transition-colors"
                    style={{
                      fontFamily: designTokens.typography.fontFamily.sans[0],
                      fontSize: '16px',
                      color: designTokens.colors.gray[6]
                    }}
                  >
                    전체
                  </button>
                )}

                {filteredActionModels.map((actionModel) => (
                  <button
                    key={actionModel.key}
                    type="button"
                    onClick={() => handleSelect(actionModel)}
                    className="w-full px-3 py-2 text-left hover:bg-gray-50 transition-colors"
                    style={{
                      fontFamily: designTokens.typography.fontFamily.sans[0],
                      fontSize: '16px',
                      color: designTokens.colors.gray[6]
                    }}
                  >
                    {actionModel.name}
                  </button>
                ))}
              </>
            )}
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div
          className="mt-1 text-sm"
          style={{
            color: designTokens.colors.error.DEFAULT,
            fontFamily: designTokens.typography.fontFamily.sans[0]
          }}
        >
          {error}
        </div>
      )}

      {/* Click Outside Handler */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  )
}

export default ActionModelSelect
